import { FC } from 'react';
import { Icon } from './icons/Icon';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';

export interface ScriptComment {
    id: string;
    lineNumber: number;
    content: string;
    createdAt: number;
    user: { id: string; displayName: string; iconUrl: string | null };
}

interface ScriptViewerProps {
    textContent: string;
    comments?: ScriptComment[];
    onCommentClick?: (comment: ScriptComment) => void;
    onAddComment?: (lineNumber: number) => void;
    isLoggedIn?: boolean;
}

export const ScriptViewer: FC<ScriptViewerProps> = ({
    textContent,
    comments = [],
    onCommentClick,
    onAddComment,
    isLoggedIn = false
}) => {
    const { playSound } = useGamenics();
    const lines = textContent.split('\n');

    return (
        <div className="w-full bg-cream-2 rounded-xl p-4 md:p-6 border-2 border-ink/15 font-sans text-ink leading-loose overflow-y-auto max-h-[60vh] scrollbar-thin scrollbar-thumb-white/10">
            {lines.map((line, index) => {
                const lineComments = comments.filter(c => c.lineNumber === index);
                return (
                    <div key={index} className="group relative flex items-start gap-4 hover:bg-cream p-1 -mx-2 px-2 rounded transition-colors">
                        <div className="text-ink-3 text-xs mt-2 w-6 text-right select-none">{index + 1}</div>
                        <div 
                            className={`flex-1 ${isLoggedIn ? 'cursor-pointer' : ''}`}
                            onClick={() => {
                                if (isLoggedIn && onAddComment) {
                                    playSound(GamenicsEvent.TAP_CONFIRM);
                                    onAddComment(index);
                                }
                            }}
                        >
                            {line || <br />}
                        </div>
                        {lineComments.length > 0 && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 z-10">
                                {lineComments.map(c => (
                                    <div 
                                        key={c.id} 
                                        className="w-6 h-6 rounded-full overflow-hidden border border-primary cursor-pointer  hover:scale-110 transition-transform bg-cream-2"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            playSound(GamenicsEvent.TAP_CONFIRM);
                                            onCommentClick?.(c);
                                        }}
                                        title={c.content}
                                    >
                                        {c.user.iconUrl ? <img src={c.user.iconUrl} alt={c.user.displayName} /> : <div className="bg-cream-2 w-full h-full text-[8px] flex items-center justify-center"><Icon name="user" size={10} className="text-ink-3" /></div>}
                                    </div>
                                ))}
                            </div>
                        )}
                        {isLoggedIn && onAddComment && lineComments.length === 0 && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        playSound(GamenicsEvent.TAP_CONFIRM);
                                        onAddComment(index);
                                    }}
                                    className="text-primary text-xs border border-primary/50 px-2 py-1 rounded-full hover:bg-primary/20 bg-cream"
                                >
                                    <Icon name="message" size={12} /> 称賛
                                </button>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
